import { useMutation, useQueryClient } from '@tanstack/react-query';
import { expensesApi, type AdmissionPharmaCostItem } from '@/api/expenses';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const EXCLUDE_WARNING =
  'Once unchecked, this cost will not be included in P&L, profitability, or any other financial report calculations.';

type Props = {
  item: Pick<AdmissionPharmaCostItem, 'id' | 'kind' | 'includedInReports'>;
};

export function PharmaCostReportInclusionCheckbox({ item }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canEdit =
    user?.role === 'HOSPITAL_ADMIN' ||
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'BUSINESS_ANALYST';

  const mutation = useMutation({
    mutationFn: (included: boolean) =>
      expensesApi.setPharmaCostReportInclusion(item.kind, item.id, included),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['expenses-admission-cost-lines'] });
      void queryClient.invalidateQueries({ queryKey: ['hospital-pnl'] });
      void queryClient.invalidateQueries({ queryKey: ['hospital-cash-flow'] });
    },
  });

  const included = item.includedInReports !== false;

  const handleChange = (checked: boolean) => {
    if (!canEdit || mutation.isPending) return;
    if (!checked) {
      const ok = window.confirm(EXCLUDE_WARNING);
      if (!ok) return;
    }
    mutation.mutate(checked);
  };

  return (
    <Checkbox
      checked={included}
      disabled={!canEdit || mutation.isPending}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onCheckedChange={handleChange}
      aria-label={included ? 'Included in reports' : 'Excluded from reports'}
      title={
        canEdit
          ? included
            ? 'Included in report calculations'
            : 'Excluded from report calculations'
          : undefined
      }
      className={cn('shrink-0', !included && 'opacity-60')}
    />
  );
}
